import PropTypes from 'prop-types';
import React, { cloneElement } from 'react';

import * as Keyboard from '../../../lib/Keyboard';

import './KeyboardHandler.scss';

/**
 * Attaches and detaches event handlers for key commands that apply when a
 * specific element has focus. Useful for making custom form elements and
 * buttons accessible.
 *
 * To use it, wrap it around the element you want to add keymappings to.  To
 * attach a single event handler, use the `keySet` `keyboardKey`, and `handler`
 * attributes.
 *
 *  <KeyboardHandler
 *    keyboardKey={Constants.Keyboard.MousetrapKeys.ENTER}
 *    keySet={Constants.Keyboard.KeySetKeys.MY_KEYSET}
 *    handler={this.doSomething}>
 *    <MyElementWithAriaAttributes/>
 *  </KeyboardHandler>
 *
 * To attach multiple handlers, set the `keySet` the same way, but use the
 * `keyMaps` attribute to pass in an array of keyMap objects.
 *
 *    <KeyboardHandler
 *      keySet={Constants.Keyboard.KeySetKeys.MY_KEYSET}
 *      keyMaps={[{
 *          keyboardKey: Constants.Keyboard.MousetrapKeys.SPACE,
 *          handler: () => { this.props.doSomething; }
 *        }, {
 *          keyboardKey: Constants.Keyboard.MousetrapKeys.RIGHT,
 *          handler: this.doSomethingElse
 *        }, {
 *          keyboardKey: Constants.Keyboard.MousetrapKeys.DOWN,
 *          handler: this.doAnotherThing
 *        }]}/>
 *    <MyElementWithAriaAttributes/>
 *  </KeyboardHandler>
 *
 * @param {node} children - a React element
 * @param {string} keySet - the name of the keySet that this key command is
 *        part of (defined in lib/Keyboard.js)
 * @param {string} keyboardKey - the key to map
 * @param {Function} handler - the function to call with the key is pressed
 * @param {object} keyMaps - an array of objects that each contain a
 *        `keyboardKey` and a `handler`
 */
class KeyboardHandler extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    keySet: PropTypes.string.isRequired,
    keyboardKey: PropTypes.string,
    handler: PropTypes.func,
    keyMaps: PropTypes.arrayOf(
      PropTypes.shape({
        keyboardKey: PropTypes.string.isRequired,
        handler: PropTypes.func.isRequired,
      }),
    ),
  };

  componentWillUnmount() {
    this.unbindHandlers();
  }

  getKeyMaps = () => {
    const { keyboardKey, handler } = this.props;
    const keyMaps = this.props.keyMaps ? this.props.keyMaps : [{ keyboardKey, handler }];
    return keyMaps;
  };

  bindHandler = ({ keyboardKey, keySet, handler }) => {
    Keyboard.bindKey({ key: keyboardKey, keySet, handler });
  };

  unbindHandler = ({ keyboardKey, keySet, handler }) => {
    Keyboard.unBindKey({ key: keyboardKey, keySet, handler });
  };

  bindHandlers = () => {
    const { keySet } = this.props;
    this.getKeyMaps().forEach(mapping => {
      this.bindHandler({ ...mapping, keySet });
    });
  };

  unbindHandlers = () => {
    const { keySet } = this.props;
    this.getKeyMaps().forEach(mapping => {
      this.unbindHandler({ ...mapping, keySet });
    });
  };

  render() {
    return (
      <div className='KeyboardHandler' onFocus={this.bindHandlers} onBlur={this.unbindHandlers}>
        {cloneElement(this.props.children)}
      </div>
    );
  }
}

export default KeyboardHandler;
